import { appMode } from '@/utils/config/mode'
import { findDuplicates } from '@/utils/helpers/findDuplicates'
import _ from '@/utils/setup/lodash'
import account from './account'
import alerts from './alerts'
import apiErrors from './api-errors'
import cities from './cities'
import cloudAccounts from './cloud-accounts'
import cloudCostManagement from './cloud-cost-management'
import credentials from './credentials'
import environment from './environment'
import errors from './errors'
import events from './events'
import externalBackends from './external-backends'
import filters from './filters'
import forms from './forms'
import infra from './infra'
import inventory from './inventory'
import kpis from './kpis'
import lang from './lang'
import legal from './legal'
import licence from './licence'
import members from './members'
import pipeline from './pipeline'
import plans from './plans'
import plugins from './plugins'
import pluralizations from './pluralizations'
import presentation from './presentation'
import roles from './roles'
import routes from './routes'
import severities from './severities'
import sorts from './sorts'
import stacks from './stacks'
import terracost from './terracost'
import time from './time'
import watchRules from './watchRules'
import workers from './workers'

const commonCycloidTerms = {
  Activity: 'Activité',
  APIKey: 'Clé API',
  APIKeys: 'Clés API',
  Appearance: 'Apparence',
  CatalogRepositories: 'Sources du catalogue',
  CatalogRepository: 'Source du catalogue',
  Component: 'Composant',
  ConfigRepositories: 'Sources de configuration',
  ConfigRepository: 'Source de configuration',
  Credential: `Information d'identification`,
  Credentials: `Informations d'identification`,
  Dashboard: 'Tableau de bord',
  Details: 'Détails',
  EnvComponent: 'Composant',
  EnvComponents: 'Composants',
  CloudAccounts: 'Comptes Cloud',
  Environment: 'Environnement',
  Environments: 'Environnements',
  Events: 'Évènements',
  InfraPolicy: 'InfraPolicy',
  InfraPolicies: 'InfraPolicies',
  Infrastructure: 'Infrastructure',
  Inventory: 'Inventaire',
  Member: 'Membre',
  Members: 'Membres',
  Metrics: 'Métriques',
  Organization: 'Organisation',
  Organizations: 'Organisations',
  Pending: 'En attente',
  Project: 'Projet',
  Projects: 'Projets',
  Resource: 'Ressource',
  Resources: 'Ressources',
  Role: 'Rôle',
  Roles: 'Rôles',
  Stack: 'Stack',
  Stacks: 'Stacks',
  Team: 'Équipe',
  Teams: 'Équipes',
  TerraformBackend: 'Backend Terraform',
  Users: 'Utilisateurs',
  Output: 'Sortie',
  Outputs: 'Sorties',
}

// Add the same set of terms with camelCased keys and lowercased values.
const camelCaseTerms = _.mapValues(_.$camelCaseKeys(commonCycloidTerms), _.toLower)
Object.assign(commonCycloidTerms, camelCaseTerms)

const fr = {
  all: 'Tout',
  about: 'À propos',
  and: 'et',
  assignOwner: 'Attribuer un propriétaire',
  Author: 'Auteur',
  clone: 'Cloner',
  color: 'Couleur',
  collapse: 'réduire',
  copy: 'Copier',
  copyToClipboard: 'Copier dans le presse-papier',
  copiedToClipboard: '{value} copié dans le presse-papier',
  comingSoon: 'bientôt disponible',
  comparedToLastWeek: 'Par rapport à la semaine dernière',
  configuration: 'Configuration',
  configure: 'Configurer',
  date: 'Date',
  Default: 'Défaut',
  development: 'développement',
  disabled: 'Désactivée',
  editor: 'Éditeur',
  enabled: 'Activée',
  Filters: 'Filtres',
  from: 'de',
  fullScreen: 'Plein écran',
  general: 'Général',
  icon: 'Icône',
  importing: 'importation',
  invite: 'Invitez',
  learnMore: 'En savoir plus',
  name: 'Nom',
  noOwner: 'aucun propriétaire',
  maintainer: 'Mainteneur',
  or: 'ou',
  other: 'Autre',
  others: 'Autres',
  owner: 'Propriétaire',
  preview: 'Aperçu',
  properties: 'Propriétés',
  provider: 'Fournisseur',
  ReadMore: 'Lire plus',
  seeMore: 'voir plus',
  Settings: 'Paramètres',
  Summary: 'Résumé',
  weekOf: 'Semaine du',
  welcomeMsg: `Bienvenue sur l'App Cycloid`,
  youAreUsing: 'Vous utilisez',
  youDoNotHavePermissionsTo: 'Vous ne disposez pas des autorisations suffisantes pour',
  info: 'Info',
  warning: 'Avertissement',
  error: 'Erreur',
  critical: 'Critique',
  read: 'Lu',
  unread: 'Non lu',
  items: 'éléments',
  lastUsed: 'Dernière utilisation',
  ...commonCycloidTerms,
  ...pluralizations,
}

// We flatten the imported objects in order to avoid sneaky clashes
// with previously defined strings (see FE#7160).
const moduleTranslations = {
  account: account.fr,
  alerts: alerts.fr,
  apiErrors: apiErrors.fr,
  cities: cities.en,
  cloudAccounts: cloudAccounts.fr,
  cloudCostManagement: cloudCostManagement.fr,
  credentials: credentials.fr,
  environment: environment.fr,
  errors: errors.fr,
  events: events.fr,
  externalBackends: externalBackends.fr,
  filters: filters.fr,
  forms: forms.fr,
  infra: infra.fr,
  inventory: inventory.fr,
  kpis: kpis.fr,
  lang: lang.fr,
  legal: legal.fr,
  licence: licence.fr,
  members: members.fr,
  pipeline: pipeline.fr,
  plans: plans.fr,
  presentation: presentation.fr,
  roles: roles.fr,
  routes: routes.fr,
  severities: severities.fr,
  sorts: sorts.fr,
  stacks: stacks.fr,
  terracost: terracost.fr,
  time: time.fr,
  watchRules: watchRules.fr,
  workers: workers.fr,
  plugins: plugins.fr,
}

// Final check for duplicate keys before exporting all translations (FE#7168).
const duplicates = findDuplicates([...Object.keys(fr), ...Object.keys(moduleTranslations)])
if (duplicates.length && appMode.isDev) {
  console.warn(`Duplicate keys found in translations export: ${duplicates.join(', ')}`)
}

export default {
  ...fr,
  ...moduleTranslations,
}
